import React, { useEffect, useRef, useState } from 'react';
import ReactFocusLock from 'react-focus-lock';
import { InputChangeEvent } from '@progress/kendo-react-inputs';

import { IProjectTypesDialogProps } from '../ProjectTypes.types';

import {
  useDisableEscape,
  useDisableRightClick,
  useHighlightInput,
  useKeyPress,
} from '../../../../hooks';
import { handleKeyDown, validateProjectTypesForm } from '../../../../utils';
import { Button, InputWrapper } from '../../../common';

import {
  DialogContent,
  StyledTitle,
  ButtonWrapper,
} from './ProjectTypesDialog.styled';
import { GenericDialog } from '../../../common/GenericDialog/GenericDialog';
import MovableDialog from '../../../common/MovableDialog';
import { projectTypesService } from '../../../../services/operations/projectService';

const ProjectTypesDialog: React.FC<IProjectTypesDialogProps> = ({
  isEdit,
  data,
  handleClose,
}) => {
  const [visible, setVisible] = useState<boolean>(false);
  const [formState, setFormState] = useState({
    IDNo: isEdit && data ? data.IDNo : 0,
    ProjType: isEdit && data ? data.ProjType : '',
    ProjDesc: isEdit && data ? data.ProjDesc : '',
  });
  const [fieldProperties, setFieldProperties] = useState<any[]>([]);
  const [isValid, setIsValid] = useState<boolean>(false);
  const [isChanged, setIsChanged] = useState<boolean>(false);

  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const formRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<any>(null);

  useHighlightInput('project-code');
  useDisableRightClick(dialogRef);
  useDisableEscape(dialogRef);

  useKeyPress(
    () => {
      if (isValid && isChanged && !isSaving && !isDeleting) {
        handleSave();
      }
    },
    () => {
      handleClose();
    },
    () => {
      toggleDialog();
    }
  );

  const fetchProject = async () => {
    const response = await projectTypesService.getProject(formState.IDNo);
    console.log(response.fieldProperties);
    setFieldProperties(response.fieldProperties);
  };

  useEffect(() => {
    fetchProject();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setIsValid(validateProjectTypesForm(formState));
  }, [formState]);

  const handleSave = async (e?: React.MouseEvent) => {
    e?.preventDefault();

    try {
      if (formState.IDNo || formState.IDNo === 0) {
        setIsSaving(true);

        const saved = await projectTypesService.CreateUpdateProject(
          isEdit ? formState.IDNo : 0,
          {
            ProjType: formState.ProjType,
            ProjDesc: formState.ProjDesc,
          }
        );

        if (saved) {
          setIsSaving(false);
          handleClose(true);
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleDelete = async (e?: React.MouseEvent) => {
    e?.preventDefault();

    try {
      if (formState.IDNo) {
        setIsDeleting(true);
        const status = await projectTypesService.deleteProject(formState.IDNo);

        if (status) {
          setIsDeleting(false);
          handleClose(true);
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleOnChange = (event: InputChangeEvent, key: string) => {
    setIsChanged(true);

    if (key === 'ProjType') {
      const formmattedValue = event.value.toUpperCase();
      setFormState((prevState) => ({
        ...prevState,
        [key]: formmattedValue.replace(/[^a-zA-Z0-9]/g, ''),
      }));
    } else {
      setFormState((prevState) => ({
        ...prevState,
        [key]: event.value,
      }));
    }
  };

  const toggleDialog = (state?: boolean) => {
    if (state) {
      handleDelete();
    }
    setVisible(!visible);
  };

  const CustomWindowTitle = () => {
    return (
      <StyledTitle>
        <span>{isEdit ? 'Edit' : 'Add'} Project Type</span>
        {isEdit && (
          <button
            tabIndex={-1}
            onClick={(e) => {
              e.preventDefault();
              toggleDialog();
            }}
            className="k-button k-button-md k-button-flat k-button-flat-base k-rounded-md k-icon-button k-window-titlebar-action"
          ></button>
        )}
      </StyledTitle>
    );
  };

  return (
    <MovableDialog
      onClose={() => handleClose()}
      className="project-types-dialog"
      title={<CustomWindowTitle />}
      ref={dialogRef}
    >
      <ReactFocusLock>
        <DialogContent>
          <div
            className="form-fields"
            onKeyDown={(e) => handleKeyDown(e, formRef)}
            ref={formRef}
          >
            <InputWrapper
              name="ProjCode"
              label="Code"
              id="project-code"
              onChange={(e) => handleOnChange(e, 'ProjType')}
              value={formState.ProjType}
              maxLength={6} // call from api
              autoComplete="off"
              width="100px"
              isDisabled={data.ProjType}
            />
            <InputWrapper
              name="ProjtDesc"
              label="Description"
              onChange={(e) => handleOnChange(e, 'ProjDesc')}
              value={formState.ProjDesc}
              maxLength={
                fieldProperties.find(
                  (field: any) => field.columnName === 'ProjDesc'
                )?.maxLength
              }
              autoComplete="off"
              width="220px"
            />
          </div>
          <ButtonWrapper>
            <Button
              onClick={(e) => {
                e.preventDefault();
                handleSave();
              }}
              disabled={!isValid || isSaving || isDeleting || !isChanged}
              id="save-btn"
              title="Save"
            >
              <>
                <u>S</u>ave
              </>
            </Button>

            <Button
              onClick={() => handleClose()}
              type="button"
              id="cancel-btn"
              title="Cancel"
              disabled={isSaving || isDeleting}
            >
              <u>C</u>ancel
            </Button>
            {isEdit && (
              <Button
                onClick={() => toggleDialog()}
                type="button"
                id="delete-btn"
                title="Delete"
                tabIndex={-1}
              >
                <u>D</u>elete
              </Button>
            )}
          </ButtonWrapper>
        </DialogContent>
      </ReactFocusLock>

      {visible && (
        <GenericDialog
          type="Confirmation"
          onCancel={() => setVisible(false)}
          onConfirmed={() => toggleDialog(true)}
          confirmButtonText="Delete"
          primaryMessage="Delete record?"
        />
      )}
    </MovableDialog>
  );
};

export default ProjectTypesDialog;
